import React from "react";
import fitclubIcon from "../../assets/fitclub-app-icon.png";
import { classNames } from "../../helpers";
import { BrandLogo, OnboardingBrandPanel } from "../onboarding/OnboardingBrand";

/**
 * Shared layout for the sign-in, password and join screens (new design, same as "Create your club account"):
 * a coloured panel on the left (lg and up) and the form on the right.
 *  - FitClub domain (club admins): the FitClub brand panel.
 *  - A club's own address or club link (members): ClubPanel, built from that club's own data
 *    (getHostHomePage: business_detail + classes): cover photo, logo, about text, classes with photos, links.
 * On phones the panel becomes a compact header (ClubHeader / the FitClub logo).
 */

export const clubOf = (club) => {
  const bd = club?.business_detail || {};
  const classes = (Array.isArray(club?.classes) ? club.classes : []).filter(
    (c) => c && !c.isDeleted && c.className && c.isClassPublic !== false && c.isPublic !== false
  );
  const withPhoto = classes.filter((c) => c.classPhoto);
  const addr = bd.address || {};
  const city = addr.city || (addr.addressName && addr.addressName !== "Business Address" ? addr.addressName : "");
  const site = (bd.workWebsite || "").trim();
  const insta = (bd.socialLink?.instagram || "").trim();
  const fullAddress = addr.description || addr.address || "";
  const withHttp = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);
  return {
    name: bd.businessName || "",
    logo: bd.logo || "",
    about: (bd.about || "").trim(),
    city,
    cover: (Array.isArray(bd.images) ? bd.images : []).find(Boolean) || withPhoto[0]?.classPhoto || "",
    classes: [...withPhoto, ...classes.filter((c) => !c.classPhoto)].slice(0, 3),
    website: site ? withHttp(site) : "",
    instagram: insta ? withHttp(insta) : "",
    mapUrl: fullAddress ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}` : "",
  };
};

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "C";

const fmtMin = (minutes) => {
  const m = Number(minutes) || 0;
  if (!m) return "";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

export function ClubLogo({ club, className = "h-16 w-16 rounded-[18px] text-2xl" }) {
  const c = clubOf(club);
  return c.logo ? (
    <img src={c.logo} alt="" className={classNames("shrink-0 bg-white object-cover shadow-lg", className)} />
  ) : (
    <span className={classNames("flex shrink-0 items-center justify-center bg-white font-black text-brown-coffee shadow-lg", className)}>{initials(c.name)}</span>
  );
}

const Social = ({ href, label, children }) =>
  href ? (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25">
      {children}
    </a>
  ) : null;

/** Left panel for members: the club's own cover, logo, about text and classes. */
export function ClubPanel({ club, badge = null }) {
  const c = clubOf(club);
  return (
    <section className="relative hidden w-[44%] shrink-0 flex-col overflow-hidden bg-brown-coffee px-12 py-11 text-white lg:flex xl:w-[42%]">
      {c.cover ? (
        <img src={c.cover} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <>
          <div className="absolute -right-36 -top-32 h-[420px] w-[420px] rounded-full bg-light-brown-coffee" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-24 h-64 w-64 rounded-full bg-portland-orange/20" aria-hidden="true" />
        </>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#2c0f23] via-[#481B3A]/75 to-[#481B3A]/20" aria-hidden="true" />

      <div className="relative flex items-center gap-3.5">
        <ClubLogo club={club} />
        <div className="min-w-0">
          <div className="line-clamp-2 text-2xl font-black leading-tight">{c.name}</div>
          {c.city && <div className="text-sm font-bold text-white/80">{c.city}</div>}
        </div>
      </div>

      <div className="relative mt-auto">
        {badge && (
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-extrabold tracking-wide">
            <span className="h-2 w-2 rounded-full bg-[#7CE0A3]" />
            {badge}
          </div>
        )}
        <h2 className="line-clamp-4 text-3xl font-black leading-[1.1] tracking-tight xl:text-[40px]">
          {c.about || `Book your classes at ${c.name}.`}
        </h2>

        {c.classes.length > 0 && (
          <>
            <div className="mt-7 text-xs font-extrabold tracking-wide text-white/80">CLASSES AT {c.name.toUpperCase()}</div>
            <ul className="mt-2.5 grid grid-cols-3 gap-3">
              {c.classes.map((k) => (
                <li key={k._id || k.className} className="overflow-hidden rounded-2xl bg-white/10 backdrop-blur-sm">
                  {k.classPhoto ? (
                    <img src={k.classPhoto} alt="" className="h-24 w-full object-cover" />
                  ) : (
                    <span className="flex h-24 w-full items-center justify-center bg-white/10 text-3xl font-black text-white/80">{k.className.slice(0, 1)}</span>
                  )}
                  <div className="px-3 py-2.5">
                    <div className="truncate text-sm font-extrabold">{k.className}</div>
                    {fmtMin(k.duration) && <div className="text-xs font-semibold text-white/75">{fmtMin(k.duration)}</div>}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-7 flex items-center justify-between gap-4">
          <div className="flex gap-2">
            <Social href={c.instagram} label="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="5" />
                <circle cx="12" cy="12" r="4" />
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" />
              </svg>
            </Social>
            <Social href={c.website} label="Website">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
              </svg>
            </Social>
            <Social href={c.mapUrl} label="Address">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" aria-hidden="true">
                <path d="M12 21s-7-6.2-7-12a7 7 0 0 1 14 0c0 5.8-7 12-7 12Z" />
                <circle cx="12" cy="9" r="2.5" />
              </svg>
            </Social>
          </div>
          <div className="flex items-center gap-2 text-[13px] font-bold text-white/75">
            Powered by
            <img src={fitclubIcon} alt="" className="h-5 w-5 rounded-[22%]" />
            <span className="font-extrabold text-white">FitClub</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Compact club header for phones and tablets (the panel is hidden below lg). */
export function ClubHeader({ club }) {
  const c = clubOf(club);
  return (
    <div className="-mx-5 -mt-8 mb-6 flex items-center gap-3 rounded-b-[28px] bg-gradient-to-br from-[#6a2d57] to-brown-coffee px-5 py-6 text-white sm:-mx-10 lg:hidden">
      <ClubLogo club={club} className="h-12 w-12 rounded-2xl text-lg" />
      <div className="min-w-0">
        <div className="truncate text-lg font-black">{c.name}</div>
        {(c.city || c.about) && <div className="truncate text-[13px] font-semibold text-white/85">{c.city || c.about}</div>}
      </div>
    </div>
  );
}

/**
 * Page layout. `club` = getHostHomePage data for a member screen; omit it for the FitClub (admin) screens.
 * `brandTitle` / `brandLine` set the FitClub panel's text; `clubBadge` adds a pill above the club text.
 */
export default function AuthShell({ club = null, brandTitle = "Welcome back.", brandLine = "Today's classes, bookings and payments in one place.", clubBadge = null, children }) {
  return (
    <div className="flex min-h-screen w-full bg-white">
      {club ? <ClubPanel club={club} badge={clubBadge} /> : <OnboardingBrandPanel badge={null} title={brandTitle} lines={[brandLine]} showFeatures={false} />}
      <section className="flex min-w-0 flex-1 flex-col justify-start px-5 py-8 sm:px-10 md:px-16 lg:justify-center xl:px-24">
        {club ? <ClubHeader club={club} /> : <BrandLogo variant="dark" className="mb-8 lg:hidden" />}
        <div className="w-full max-w-md lg:mx-auto">{children}</div>
      </section>
    </div>
  );
}

/* ---------- small shared pieces for these screens ---------- */

export function AuthHeading({ eyebrow, title, children }) {
  return (
    <div>
      {eyebrow && <p className="text-xs font-extrabold tracking-wide text-indigo-bz sm:text-[13px]">{eyebrow}</p>}
      <h1 className="mt-2 text-3xl font-black tracking-tight text-gray-900 sm:text-[38px] sm:leading-tight">{title}</h1>
      {children && <p className="mt-2 text-base text-gray-600 sm:text-[17px]">{children}</p>}
    </div>
  );
}

export function PrimaryAuthButton({ children, busy = false, disabled = false, type = "submit", onClick }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={busy || disabled}
      className="flex h-[54px] w-full items-center justify-center gap-2 rounded-2xl bg-brown-coffee text-[17px] font-extrabold text-white transition-colors hover:bg-light-brown-coffee disabled:opacity-60"
    >
      {busy && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
      {children}
    </button>
  );
}

export function GoogleButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-[54px] w-full items-center justify-center gap-3 rounded-2xl border border-[#E3DCE6] bg-white text-base font-extrabold text-gray-900 hover:bg-[#FAF8FB]"
    >
      {/* Original FitClub web Google icon */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="26px" height="26px" aria-hidden="true"><linearGradient id="95yY7w43Oj6n2vH63j6HJb" x1="29.401" x2="29.401" y1="4.064" y2="106.734" gradientTransform="matrix(1 0 0 -1 0 66)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#ff5840"></stop><stop offset=".007" stopColor="#ff5840"></stop><stop offset=".989" stopColor="#fa528c"></stop><stop offset="1" stopColor="#fa528c"></stop></linearGradient><path fill="url(#95yY7w43Oj6n2vH63j6HJb)" d="M47.46,15.5l-1.37,1.48c-1.34,1.44-3.5,1.67-5.15,0.6c-2.71-1.75-6.43-3.13-11-2.37	c-4.94,0.83-9.17,3.85-11.64, 7.97l-8.03-6.08C14.99,9.82,23.2,5,32.5,5c5,0,9.94,1.56,14.27,4.46	C48.81,10.83,49.13,13.71,47.46,15.5z"></path><linearGradient id="95yY7w43Oj6n2vH63j6HJc" x1="12.148" x2="12.148" y1=".872" y2="47.812" gradientTransform="matrix(1 0 0 -1 0 66)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#feaa53"></stop><stop offset=".612" stopColor="#ffcd49"></stop><stop offset="1" stopColor="#ffde44"></stop></linearGradient><path fill="url(#95yY7w43Oj6n2vH63j6HJc)" d="M16.01,30.91c-0.09,2.47,0.37,4.83,1.27,6.96l-8.21,6.05c-1.35-2.51-2.3-5.28-2.75-8.22	c-1.06-6.88,0.54-13.38, 3.95-18.6l8.03,6.08C16.93,25.47,16.1,28.11,16.01,30.91z"></path><linearGradient id="95yY7w43Oj6n2vH63j6HJd" x1="29.76" x2="29.76" y1="32.149" y2="-6.939" gradientTransform="matrix(1 0 0 -1 0 66)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#42d778"></stop><stop offset=".428" stopColor="#3dca76"></stop><stop offset="1" stopColor="#34b171"></stop></linearGradient><path fill="url(#95yY7w43Oj6n2vH63j6HJd)" d="M50.45,51.28c-4.55,4.07-10.61,6.57-17.36,6.71C22.91,58.2,13.66,52.53,9.07,43.92l8.21-6.05	C19.78,43.81, 25.67,48,32.5,48c3.94,0,7.52-1.28,10.33-3.44L50.45,51.28z"></path><linearGradient id="95yY7w43Oj6n2vH63j6HJe" x1="46" x2="46" y1="3.638" y2="35.593" gradientTransform="matrix(1 0 0 -1 0 66)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#155cde"></stop><stop offset=".278" stopColor="#1f7fe5"></stop><stop offset=".569" stopColor="#279ceb"></stop><stop offset=".82" stopColor="#2cafef"></stop><stop offset="1" stopColor="#2eb5f0"></stop></linearGradient><path fill="url(#95yY7w43Oj6n2vH63j6HJe)" d="M59,31.97c0.01,7.73-3.26,14.58-8.55,19.31l-7.62-6.72c2.1-1.61,3.77-3.71,4.84-6.15 c0.29-0.66-0.2-1.41-0.92-1.41H37c-2.21,0-4-1.79-4-4v-2c0-2.21,1.79-4,4-4h17C56.75,27,59,29.22,59,31.97z"></path></svg>
      Continue with Google
    </button>
  );
}

export function OrDivider({ children }) {
  return (
    <div className="flex items-center gap-3.5 text-sm font-bold text-gray-500">
      <span className="h-px flex-1 bg-[#ECE8EE]" />
      {children}
      <span className="h-px flex-1 bg-[#ECE8EE]" />
    </div>
  );
}

export function StepBar({ step, of = 3 }) {
  return (
    <div>
      <p className="flex items-center gap-2 text-xs font-extrabold tracking-wide text-indigo-bz sm:text-[13px]">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-bz text-[11px] text-white">{step}</span>
        STEP {step} OF {of}
      </p>
      <div className="mt-3 flex gap-1.5" aria-hidden="true">
        {Array.from({ length: of }).map((_, i) => (
          <span key={i} className={classNames("h-1.5 flex-1 rounded-full", i < step ? "bg-indigo-bz" : "bg-[#EDE5F0]")} />
        ))}
      </div>
    </div>
  );
}
